/**
 * UpgradeSheetC - Bottom Sheet Variant (v2)
 * 
 * Mobile-native bottom sheet/drawer with progressive disclosure.
 * Features: Drag handle, swipe-down dismiss, thumb-friendly buttons.
 * 
 * v2 Changes:
 * - 2 options (Monthly + Lifetime) instead of 3
 * - Primary CTA is now "Try Free" (monthly trial)
 * - Trust signals
 * - Outcome-focused copy
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BenefitsList } from './shared/BenefitsList';
import { PRICING, BENEFITS, PricingPlan, TRUST_SIGNALS } from './shared/constants';
import { useLifetimeStatus } from '@/hooks/useLifetimeStatus';
import { useLanguage } from '@/i18n';
import './UpgradeSheetC.css';

interface UpgradeSheetCProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (plan: PricingPlan) => void;
}

export function UpgradeSheetC({ 
  isOpen, 
  onClose, 
  onSelectPlan,
}: UpgradeSheetCProps) {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  
  // Fetch real lifetime status from API
  const lifetimeStatus = useLifetimeStatus();

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setTranslateY(0);
    }
  }, [isOpen]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Touch handlers for swipe-to-dismiss
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - dragStartY.current;
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setTranslateY(diff);
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    // If dragged more than 100px, close
    if (translateY > 100) {
      onClose();
    }
    setTranslateY(0);
  }, [translateY, onClose]);

  // Mouse handlers for desktop testing
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragStartY.current = e.clientY;
    setIsDragging(true);
  }, []);

  // Global mouse handlers for drag (attached to window)
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const diff = e.clientY - dragStartY.current;
        if (diff > 0) {
          setTranslateY(diff);
        }
      };
      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        if (translateY > 100) {
          onClose();
        }
        setTranslateY(0);
      };
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, translateY, onClose]);

  const handleSelectPlan = (plan: PricingPlan) => {
    onSelectPlan(plan);
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className={`upgrade-sheet-backdrop ${isOpen ? 'upgrade-sheet-backdrop--open' : ''}`}
      onClick={onClose}
    >
      <div 
        ref={sheetRef}
        className={`upgrade-sheet ${isExpanded ? 'upgrade-sheet--expanded' : ''}`}
        style={{ 
          transform: `translateY(${translateY}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease',
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-sheet-title"
      >
        {/* Drag handle */}
        <div 
          className="upgrade-sheet-handle"
          onMouseDown={handleMouseDown}
        >
          <div className="upgrade-sheet-handle-bar" />
        </div>

        {/* Compact view */}
        <div className="upgrade-sheet-compact">
          {/* Header with badge */}
          <div className="upgrade-sheet-header">
            <div className="upgrade-sheet-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
              PRO
            </div>
            <h2 id="upgrade-sheet-title" className="upgrade-sheet-title">
              {t('funnel.modal_title_default')}
            </h2>
            <p className="upgrade-sheet-subtitle">
              {t('funnel.sheet_subtitle')}
            </p>
          </div>

          {/* Quick benefits preview (v2: outcome-focused) */}
          <div className="upgrade-sheet-benefits-preview">
            {BENEFITS.slice(0, 2).map((benefit) => (
              <div key={benefit.id} className="benefit-preview-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{t(benefit.text)}</span>
              </div>
            ))}
            <button 
              className="upgrade-sheet-expand-btn"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? t('funnel.show_less') : t('funnel.more_benefits', { count: BENEFITS.length - 2 })}
            </button>
          </div>

          {/* Expanded benefits (shown when expanded) */}
          {isExpanded && (
            <div className="upgrade-sheet-benefits-full">
              <BenefitsList variant="compact" />
            </div>
          )}

          {/* v2: Primary CTA - Monthly Trial */}
          <button 
            className="upgrade-sheet-cta upgrade-sheet-cta--primary"
            onClick={() => handleSelectPlan('monthly')}
          >
            <span className="cta-label">{t('funnel.monthly_cta')}</span>
            <span className="cta-sublabel">{t('funnel.then_price', { price: t(PRICING.monthly.label) })}</span>
          </button>

          {/* v2: Trust signal */}
          <div className="upgrade-sheet-trust">
            {t(TRUST_SIGNALS.noPaymentToday)} - {t(TRUST_SIGNALS.cancelAnytime)}
          </div>

          {/* v2: Lifetime option */}
          <div className="upgrade-sheet-secondary">
            <span className="upgrade-sheet-or">{t('funnel.or')}</span>
            <button
              className="upgrade-sheet-option upgrade-sheet-option--lifetime"
              onClick={() => handleSelectPlan('lifetime')}
            >
              <span className="lifetime-label">{t('funnel.lifetime_access_price', { price: PRICING.lifetime.label })}</span>
              <span className="lifetime-remaining">{t('funnel.scarcity', { remaining: lifetimeStatus.remaining, total: lifetimeStatus.total })}</span>
            </button>
          </div>

          {/* Dismiss link */}
          <button className="upgrade-sheet-dismiss" onClick={onClose}>
            {t('funnel.maybe_later')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
